// VideoView.jsx

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

// Services
import { resolvePlayableVideoUrl } from 'services/storage/StorageAccessService';

// CSS
import './VideoView.css';

// Components
import Hls from 'hls.js';

/**
 * VideoView
 * 
 * Displays a video node with a blurred background copy.
 * Supports HLS (.m3u8) and standard video URLs.
 *
 * @returns {ReactNode} The rendered component.
 */
const VideoView = forwardRef(({
  node,
  pagePlaybackContext = null,
  showControls = true,
  autoplay = true,
  autosize = true,
  loop = true,
}, ref) => {

  const videoRef = useRef(null);
  const blurredVideoRef = useRef(null);
  const [isVertical, setIsVertical] = useState(true);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [playbackError, setPlaybackError] = useState('');
  const [isResolvingPlaybackUrl, setIsResolvingPlaybackUrl] = useState(false);

  // Helper to setup video with HLS or direct src
  const setupVideo = (videoElement, url) => {
    const isSecureManifest = typeof url === 'string' && url.includes('/secureHlsManifest?ticket=');
    const isDirectStorageManifest = typeof url === 'string'
      && url.includes('storage.googleapis.com')
      && url.includes('.m3u8');
    const isHls = isSecureManifest
      || (typeof url === 'string' && url.includes('.m3u8'));

    if (isDirectStorageManifest) {
      console.warn('[page-video] refusing direct Storage HLS manifest URL', url);
      return () => {};
    }

    if (isHls && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(videoElement);
      return () => hls.destroy();
    } else {
      videoElement.src = url;
      return () => {         videoElement.src = ''; };
    }
  };

  useEffect(() => {
    let cancelled = false;

    setPlaybackUrl(null);
    setPlaybackError('');
    setIsResolvingPlaybackUrl(Boolean(node));

    (async () => {
      if (!node) {
        if (!cancelled) {
          setIsResolvingPlaybackUrl(false);
        }
        return;
      }

      try {
        const url = await resolvePlayableVideoUrl(node, pagePlaybackContext);
        if (cancelled) return;

        if (!url) {
          setPlaybackError('Unable to load video playback URL.');
          setPlaybackUrl(null);
          return;
        }

        setPlaybackUrl(url);
      } catch (error) {
        if (cancelled) return;
        console.warn('[page-video] resolvePlayableVideoUrl failed', error);
        setPlaybackError(error?.message || 'Unable to load video playback URL.');
        setPlaybackUrl(null);
      } finally {
        if (!cancelled) {
          setIsResolvingPlaybackUrl(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [node, pagePlaybackContext]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return undefined;

    let hls = null;

    const cleanup = () => {
      if (hls) {
        hls.destroy();
        hls = null;
      }
    };

    const isSecureManifest = typeof playbackUrl === 'string' && playbackUrl.includes('/secureHlsManifest?ticket=');
    const isDirectStorageManifest = typeof playbackUrl === 'string'
      && playbackUrl.includes('storage.googleapis.com')
      && playbackUrl.includes('.m3u8');
    const isHls = isSecureManifest
      || (typeof playbackUrl === 'string' && playbackUrl.includes('.m3u8'));

    if (isDirectStorageManifest) {
      console.warn('[page-video] refusing direct Storage HLS manifest URL', playbackUrl);
      setPlaybackError('Video playback URL is not available for this page surface.');
      return cleanup;
    }

    if (isHls && Hls.isSupported()) {
      hls = new Hls();
      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.warn('[page-video] hls.js error', data);
        if (data?.fatal) {
          const detail = data?.details || data?.type || 'unknown';
          setPlaybackError(`Unable to start video playback (${detail}).`);
        }
      });
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
    } else if (isHls) {
      video.src = playbackUrl;
    } else {
      video.src = playbackUrl;
    }

    const onMetadata = async () => {
      const vertical = video.videoHeight > video.videoWidth;
      setIsVertical(vertical);

      if (autoplay) {
        try {
          await video.play();
        } catch (err) {
          console.warn('Autoplay failed:', err);
        }
      }
    };

    video.addEventListener('loadedmetadata', onMetadata);
    return () => {
      video.removeEventListener('loadedmetadata', onMetadata);
      cleanup();
    };
  }, [playbackUrl, autoplay]);

  // Blurred background video setup
  useEffect(() => {
    const blurredVideo = blurredVideoRef.current;
    if (!blurredVideo || !playbackUrl || autosize) return;

    const cleanup = setupVideo(blurredVideo, playbackUrl);
    return cleanup;
  }, [autosize, playbackUrl]);

  useImperativeHandle(ref, () => ({
    play: () => videoRef.current?.play(),
    pause: () => videoRef.current?.pause(),
    setMuted: (value) => {
      if (videoRef.current) videoRef.current.muted = value;
    },
    isPlaying: () => {
      const video = videoRef.current;
      return video && !video.paused && !video.ended;
    },
  }));

  if (!node) return null;

  if (isResolvingPlaybackUrl) {
    return (
      <div
        className="video-view-state"
        style={{
          padding: autosize ? '12px 0' : undefined,
        }}
      >
        Loading video...
      </div>
    );
  }

  if (playbackError) {
    return (
      <div
        className="video-view-state video-view-state-error"
        style={{
          padding: autosize ? '12px 0' : undefined,
        }}
      >
        {playbackError}
      </div>
    );
  }

  if (!playbackUrl) return null;

  return (
    <div
      className="video-view-container"
      style={{
        height: autosize ? 'auto' : '100%',
        overflow: autosize ? 'visible' : 'hidden',
      }}
    >

      {/* BLURRED */}
      {!autosize &&
        <video
          className="video-view-blurred"
          ref={blurredVideoRef}
          autoPlay
          loop={loop}
          muted
          playsInline
        />
      }

      {/* FOREGROUND */}
      <video
        className={`video-view-foreground ${autosize ? '' : (isVertical ? 'vertical' : 'horizontal')}`}
        style={autosize ? { position: 'static', width: '100%', height: 'auto', objectFit: 'contain' } : {}}
        ref={videoRef}
        muted
        loop={loop}
        playsInline
        controls={showControls}
      />

    </div>
  );
});

// Dev Tools
VideoView.displayName = 'VideoView';

export default VideoView;
